import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axiosInstance from '../../config/axiosApi/axiosApi.jsx';
import APIs from '../../Constants/apis.js';
import Preloader from '../../components/CustomComponents/preLoad/preLoader.js';
import showErrorToast from '../../components/CustomComponents/toast/toast.js';
import { logout } from '../../app/auth/authSlice.js';

const EMPTY_STATS = {
	companies: { total: 0, active: 0 },
	packages: { total: 0, active: 0 },
	quotes: { total: 0, active: 0 },
	subscriptions: { total: 0, active: 0 },
	invoices: { total: 0, paid: 0, pending: 0, overdue: 0 },
};

function Home() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState(EMPTY_STATS);

	const fetchDashboardStats = useCallback(async () => {
		try {
			setLoading(true);

			const response = await axiosInstance.get(APIs.GET_DASHBOARD_STATS);

			if (response.status >= 400) {
				showErrorToast(response.data?.message || 'Failed to load dashboard statistics');
				return;
			}

			const payload = response.data?.data ?? response.data;
			const ok = response.data?.status !== false && payload && typeof payload === 'object';
			if (ok) {
				const data = payload;
				setStats({
					companies: {
						total: data.totalCompanies ?? data.TotalCompanies ?? 0,
						active: data.activeCompanies ?? data.ActiveCompanies ?? 0,
					},
					packages: {
						total: data.totalPackages ?? data.TotalPackages ?? 0,
						active: data.activePackages ?? data.ActivePackages ?? 0,
					},
					quotes: {
						total: data.totalQuotes ?? data.TotalQuotes ?? 0,
						active: data.activeQuotes ?? data.ActiveQuotes ?? 0,
					},
					subscriptions: {
						total: data.totalSubscriptions ?? data.TotalSubscriptions ?? 0,
						active: data.activeSubscriptions ?? data.ActiveSubscriptions ?? 0,
					},
					invoices: {
						total: data.totalInvoices ?? data.TotalInvoices ?? 0,
						paid: data.paidInvoices ?? data.PaidInvoices ?? 0,
						pending: data.pendingInvoices ?? data.PendingInvoices ?? 0,
						overdue: data.overdueInvoices ?? data.OverdueInvoices ?? 0,
					},
				});
			}
		} catch (err) {
			console.error(err);
			if (err.response && err.response.status === 401) {
				dispatch(logout());
				setTimeout(() => {
					navigate('/user/login', { replace: true });
				}, 1000);
			} else {
				showErrorToast('Failed to fetch dashboard statistics');
			}
		} finally {
			setLoading(false);
		}
	}, [dispatch, navigate]);

	useEffect(() => {
		fetchDashboardStats();
	}, [fetchDashboardStats]);

	const subscriptionItems = [
		{
			key: 'packages',
			label: 'Packages',
			value: stats.packages.total,
			sub: `${stats.packages.active} active`,
			icon: 'fa-box',
			iconClass: 'dashboard-v1-tender-icon-deposit',
		},
		{
			key: 'subscriptions',
			label: 'Subscriptions',
			value: stats.subscriptions.total,
			sub: `${stats.subscriptions.active} active`,
			icon: 'fa-file-contract',
			iconClass: 'dashboard-v1-tender-icon-card',
		},
	];

	const companyContentItems = [
		{
			key: 'companies-total',
			label: 'Total Companies',
			value: stats.companies.total,
			icon: 'fa-building',
			iconClass: 'dashboard-v1-tender-icon-card',
			link: '/company/manageCompany',
		},
		{
			key: 'companies-active',
			label: 'Active Companies',
			value: stats.companies.active,
			icon: 'fa-check-circle',
			iconClass: 'dashboard-v1-tender-icon-cash',
			link: '/company/manageCompany',
		},
		{
			key: 'quotes-total',
			label: 'Total Quotes',
			value: stats.quotes.total,
			icon: 'fa-quote-left',
			iconClass: 'dashboard-v1-tender-icon-online',
			link: '/quotes/manageQuotes',
		},
		{
			key: 'quotes-active',
			label: 'Active Quotes',
			value: stats.quotes.active,
			icon: 'fa-check-circle',
			iconClass: 'dashboard-v1-tender-icon-cash',
			link: '/quotes/manageQuotes',
		},
	];

	const invoiceItems = [
		{
			key: 'total',
			label: 'Total',
			value: stats.invoices.total,
			icon: 'fa-file-invoice',
			iconClass: 'dashboard-v1-tender-icon-card',
		},
		{
			key: 'paid',
			label: 'Paid',
			value: stats.invoices.paid,
			icon: 'fa-check-circle',
			iconClass: 'dashboard-v1-tender-icon-cash',
		},
		{
			key: 'pending',
			label: 'Pending',
			value: stats.invoices.pending,
			icon: 'fa-clock',
			iconClass: 'dashboard-v1-tender-icon-online',
		},
		{
			key: 'overdue',
			label: 'Overdue',
			value: stats.invoices.overdue,
			icon: 'fa-exclamation-triangle',
			iconClass: 'dashboard-v1-tender-icon-deposit',
		},
	];

	const quickLinks = [
		{ to: '/company/manageCompany', icon: 'fa-building', label: 'Companies' },
		{ to: '/quotes/manageQuotes', icon: 'fa-quote-left', label: 'Quotes' },
		{ to: '/allergen/manageAllergens', icon: 'fa-exclamation-triangle', label: 'Allergens' },
		{ to: '/twilio/platformSettings', icon: 'fa-comment-sms', label: 'Twilio' },
	];

	return (
		<div className="dashboard-v1">
			<ol className="breadcrumb">
				<li className="breadcrumb-item">
					<Link to="/dashboard">Home</Link>
				</li>
				<li className="breadcrumb-item active">Dashboard</li>
			</ol>

			<div className="row mb-3">
				<div className="col-12">
					<div className="dashboard-v1-header">
						<div>
							<h1 className="dashboard-v1-title">
								Super Admin Dashboard
								<small>Platform overview &amp; statistics</small>
							</h1>
						</div>
					</div>
				</div>
			</div>

			{loading ? (
				<div className="dashboard-v1-page-loader" aria-busy="true" role="status" aria-live="polite">
					<Preloader />
				</div>
			) : (
				<>
					{/* KPI tiles + quick links */}
					<div className="row g-3 mb-3">
						<div className="col-12 col-xl-8">
							<div className="dashboard-v1-kpi-grid">
								<div className="dashboard-v1-kpi dashboard-v1-kpi-blue">
									<div className="dashboard-v1-kpi-inner">
										<div className="dashboard-v1-kpi-content">
											<div className="dashboard-v1-kpi-label">Companies</div>
											<div className="dashboard-v1-kpi-value">{stats.companies.total}</div>
											<div className="dashboard-v1-kpi-order-count">
												{stats.companies.active} active
											</div>
										</div>
										<div className="dashboard-v1-kpi-icon">
											<i className="fa fa-building"></i>
										</div>
									</div>
								</div>
								<div className="dashboard-v1-kpi dashboard-v1-kpi-orange">
									<div className="dashboard-v1-kpi-inner">
										<div className="dashboard-v1-kpi-content">
											<div className="dashboard-v1-kpi-label">Quotes</div>
											<div className="dashboard-v1-kpi-value">{stats.quotes.total}</div>
											<div className="dashboard-v1-kpi-order-count">
												{stats.quotes.active} active
											</div>
										</div>
										<div className="dashboard-v1-kpi-icon">
											<i className="fa fa-quote-left"></i>
										</div>
									</div>
								</div>
								<div className="dashboard-v1-kpi dashboard-v1-kpi-teal">
									<div className="dashboard-v1-kpi-inner">
										<div className="dashboard-v1-kpi-content">
											<div className="dashboard-v1-kpi-label">Subscriptions</div>
											<div className="dashboard-v1-kpi-value">{stats.subscriptions.total}</div>
											<div className="dashboard-v1-kpi-order-count">
												{stats.subscriptions.active} active
											</div>
										</div>
										<div className="dashboard-v1-kpi-icon">
											<i className="fa fa-file-contract"></i>
										</div>
									</div>
								</div>
								<div className="dashboard-v1-kpi dashboard-v1-kpi-indigo">
									<div className="dashboard-v1-kpi-inner">
										<div className="dashboard-v1-kpi-content">
											<div className="dashboard-v1-kpi-label">Invoices</div>
											<div className="dashboard-v1-kpi-value">{stats.invoices.total}</div>
											<div className="dashboard-v1-kpi-order-count">
												{stats.invoices.paid} paid / {stats.invoices.pending} pending
											</div>
										</div>
										<div className="dashboard-v1-kpi-icon">
											<i className="fa fa-file-invoice-dollar"></i>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="col-12 col-xl-4">
							<div className="dashboard-v1-sidebar-card dashboard-v1-card-compact dashboard-v1-card-fill h-100">
								<div className="dashboard-v1-section-header">
									<h3>Quick Links</h3>
								</div>
								<div className="dashboard-v1-section-body">
									<div className="dashboard-v1-tender-grid dashboard-v1-tender-grid-2col">
										{quickLinks.map((link) => (
											<Link
												key={link.to}
												to={link.to}
												className="dashboard-v1-tender-item text-decoration-none"
											>
												<div className="dashboard-v1-tender-icon dashboard-v1-order-type-icon">
													<i className={`fa ${link.icon}`}></i>
												</div>
												<div className="dashboard-v1-tender-label">{link.label}</div>
												<div className="dashboard-v1-tender-value">
													<i className="fa fa-arrow-right" aria-hidden></i>
												</div>
											</Link>
										))}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Overview sections */}
					<div className="row g-3 mb-3 dashboard-v1-row-align-top">
						<div className="col-12 col-xl-4">
							<div className="dashboard-v1-sidebar-card dashboard-v1-card-compact dashboard-v1-card-fill">
								<div className="dashboard-v1-section-header">
									<h3>Subscription System</h3>
								</div>
								<div className="dashboard-v1-section-body">
									<div className="dashboard-v1-tender-grid dashboard-v1-tender-grid-stack">
										{subscriptionItems.map((item) => (
											<div key={item.key} className="dashboard-v1-tender-item">
												<div className={`dashboard-v1-tender-icon ${item.iconClass}`}>
													<i className={`fa ${item.icon}`}></i>
												</div>
												<div className="dashboard-v1-tender-label">{item.label}</div>
												<div className="dashboard-v1-tender-meta">
													<div className="dashboard-v1-tender-value">{item.value}</div>
													<div className="dashboard-v1-tender-sub">{item.sub}</div>
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>

						<div className="col-12 col-xl-4">
							<div className="dashboard-v1-sidebar-card dashboard-v1-card-compact dashboard-v1-card-fill">
								<div className="dashboard-v1-section-header dashboard-v1-section-header-with-action">
									<h3>Companies &amp; Content</h3>
									<Link to="/company/manageCompany" className="btn btn-sm btn-outline-theme">
										View
									</Link>
								</div>
								<div className="dashboard-v1-section-body">
									<div className="dashboard-v1-tender-grid dashboard-v1-tender-grid-2col">
										{companyContentItems.map((item) => (
											<Link
												key={item.key}
												to={item.link}
												className="dashboard-v1-tender-item text-decoration-none"
											>
												<div className={`dashboard-v1-tender-icon ${item.iconClass}`}>
													<i className={`fa ${item.icon}`}></i>
												</div>
												<div className="dashboard-v1-tender-label">{item.label}</div>
												<div className="dashboard-v1-tender-value">{item.value}</div>
											</Link>
										))}
									</div>
								</div>
							</div>
						</div>

						<div className="col-12 col-xl-4">
							<div className="dashboard-v1-sidebar-card dashboard-v1-card-compact dashboard-v1-card-fill">
								<div className="dashboard-v1-section-header">
									<h3>Invoice Management</h3>
								</div>
								<div className="dashboard-v1-section-body">
									<div className="dashboard-v1-tender-grid dashboard-v1-tender-grid-2col">
										{invoiceItems.map((item) => (
											<div key={item.key} className="dashboard-v1-tender-item">
												<div className={`dashboard-v1-tender-icon ${item.iconClass}`}>
													<i className={`fa ${item.icon}`}></i>
												</div>
												<div className="dashboard-v1-tender-label">{item.label}</div>
												<div className="dashboard-v1-tender-value">{item.value}</div>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
}

export default Home;
